import { ärMenySynlig } from 'src/layouts/nav-permissions';
import { tillSynlighetskarta } from 'src/hooks/use-meny-installningar';

import type { MenyInställning } from 'src/hooks/use-meny-installningar';
import type { UppgiftsTyp } from './myndighetsdatum';

// ----------------------------------------------------------------------

/**
 * Vilka moduler Att göra får fråga ut, och vilka uppgifter den får visa.
 *
 * Listan hämtade tidigare sina sekundära källor villkorslöst: kundfakturor, inköpsunderlag,
 * egenkontroll, löntagare, arbetsgivardeklarationer, momsdeklarationer, inkomstdeklarationer
 * och stämmoarkivet, vid varje sidladdning och för varje förening. För en förening som kör
 * lanseringsytan är merparten av dem parkerade moduler som aldrig kan svara med något, och
 * `/api/skatt/v1/arbetsgivardeklarationer` och `/inkomstdeklarationer` har dessutom en egen,
 * hårdare rate limiting — de började svara 429 under helt vanlig navigering.
 *
 * <p><b>Sonden och uppgiften hänger ihop och stängs av tillsammans.</b> Det är inte en detalj:
 * myndighetsdatumen räknas fram lokalt, medan avbockningen ("den här momsdeklarationen är redan
 * inlämnad") kommer ur sonden. Stängs bara sonden av blir resultatet en påminnelse som aldrig
 * kan bli klar — sämre än att fråga för mycket. Därför avgör samma menynyckel båda.
 */

/** Menynycklarna Att göra bryr sig om. Värdet är nyckeln i `MENY_KATALOG`. */
export const SONDNYCKEL = {
  fakturor: 'fakturor',
  skatt: 'skatt',
  lön: 'lon',
  styrelse: 'styrelse',
  fastighet: 'fastighet',
} as const;

/**
 * Menyn varje uppgiftstyp hör till. `null` betyder att typen alltid visas.
 *
 * `FASTIGHETSDEKLARATION` gäller den inloggades eget småhus och inte föreningen alls — samma
 * skäl som gör att dess hämtning gateas på användaren och inte på `föreningsId`.
 * `STÄMMA` hör till styrelsen, som ingår i lanseringsytan.
 */
export const UPPGIFT_MENYNYCKEL: Record<UppgiftsTyp, string | null> = {
  ARBETSGIVARDEKLARATION: SONDNYCKEL.skatt,
  MOMSDEKLARATION: SONDNYCKEL.skatt,
  PRELIMINÄRSKATT: SONDNYCKEL.skatt,
  INKOMSTDEKLARATION: SONDNYCKEL.skatt,
  ÅRSREDOVISNING: SONDNYCKEL.skatt,
  KONTROLLUPPGIFT: SONDNYCKEL.skatt,
  STÄMMA: SONDNYCKEL.styrelse,
  FASTIGHETSDEKLARATION: null,
};

export type Sondering = {
  /** Obetalda kund- och leverantörsfakturor. */
  fakturor: boolean;
  /** Bokförda arbetsgivardeklarationer, inlämnade moms- och inkomstdeklarationer. */
  skatt: boolean;
  /** Antalet löntagare, som avgör om organisationen alls är arbetsgivare. */
  lön: boolean;
  /** Genomförda stämmor ur styrelsearkivet. */
  styrelse: boolean;
  /** Egenkontrollens förfallodagar. */
  fastighet: boolean;
  /** Om en uppgiftstyp hör till en modul organisationen har påslagen. */
  visaUppgift: (typ: UppgiftsTyp) => boolean;
};

/** Ingen sondering alls. Gäller innan menyinställningarna är inlästa. */
export const INGEN_SONDERING: Sondering = {
  fakturor: false,
  skatt: false,
  lön: false,
  styrelse: false,
  fastighet: false,
  visaUppgift: (typ) => UPPGIFT_MENYNYCKEL[typ] === null,
};

/**
 * Vad som får sonderas för en organisation.
 *
 * @param inställningar organisationens sparade menyval, från `useMenyInställningar`
 * @param klar om de valen hör till den organisation vi frågar om. Är de inte inlästa ännu
 *   sonderas ingenting: att gissa "påslagen" hade gett tillbaka precis de anrop den här
 *   modulen finns för att slippa, och att gissa "avstängd" hade dolt uppgifter som sedan
 *   dyker upp en halv sekund senare.
 */
export function byggSondering(inställningar: MenyInställning[], klar: boolean): Sondering {
  if (!klar) {
    return INGEN_SONDERING;
  }

  const synlighet = tillSynlighetskarta(inställningar);
  const synlig = (nyckel: string) => ärMenySynlig(nyckel, synlighet);

  return {
    fakturor: synlig(SONDNYCKEL.fakturor),
    skatt: synlig(SONDNYCKEL.skatt),
    lön: synlig(SONDNYCKEL.lön),
    styrelse: synlig(SONDNYCKEL.styrelse),
    fastighet: synlig(SONDNYCKEL.fastighet),
    visaUppgift: (typ) => {
      const nyckel = UPPGIFT_MENYNYCKEL[typ];
      return nyckel === null || synlig(nyckel);
    },
  };
}
